import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  actionTypes,
} from 'redux-resource';

import * as actions from 'chronos-actions';
import * as selectors from 'chronos-selectors';
import {
  jiraApi,
} from 'chronos-api';

import {
  throwError,
} from './helpers';


function* fetchAllBaseDependenciesOptions({
  apiRequest,
  getApiRequestParams,
  query,
  baseItem,
  values = [],
}) {
  const response = yield eff.call(
    apiRequest,
    {
      params: getApiRequestParams({
        query,
        item: baseItem,
        values,
      }),
    },
  );
  if (response.isLast) {
    return [
      ...values,
      ...response.values,
    ];
  }
  return yield eff.call(
    fetchAllBaseDependenciesOptions,
    {
      apiRequest,
      getApiRequestParams,
      query,
      baseItem,
      values: [
        ...values,
        ...response.values,
      ],
    },
  );
}

function* fetchFilterResources({
  type = 'simple',
  apiRequest,
  baseApiRequest,
  query,
  getApiRequestParams,
  getBaseApiRequestsParams,
  getBaseValues,
  getOption,
  getResource,
  getIncludedResources,
  isHasMore,
  loadedQty,
  getValues,
}) {
  if (type === 'depending') {
    const baseResponse = yield eff.call(
      baseApiRequest,
      getBaseApiRequestsParams({
        query,
        loadedQty,
      }),
    );
    const baseValues = getBaseValues(baseResponse);
    const values = yield eff.all(
      baseValues.map(
        baseItem => (
          eff.call(
            fetchAllBaseDependenciesOptions,
            {
              apiRequest,
              getApiRequestParams,
              baseItem,
              query,
            },
          )
        ),
      ),
    );
    return (
      values.reduce(
        (acc, items, i) => {
          acc.options.push({
            label: baseValues[i].label,
            options: items.map((item) => {
              acc.resources.push(getResource(item));
              return getOption(item);
            }),
          });
          return acc;
        },
        {
          options: [],
          includedResources: getIncludedResources({
            baseResponse,
          }),
          resources: [],
          hasMore: isHasMore({
            loadedQty,
            values,
            baseResponse,
          }),
        },
      )
    );
  }
  const response = yield eff.call(
    apiRequest,
    {
      params: getApiRequestParams({
        query,
        loadedQty,
      }),
    },
  );
  const values = getValues(response) || [];
  return (
    values.reduce(
      (acc, item) => {
        acc.options.push(getOption(item));
        acc.resources.push(getResource(item));
        return acc;
      },
      {
        options: [],
        resources: [],
        hasMore: isHasMore({
          loadedQty,
          values,
          response,
        }),
      },
    )
  );
}

function* fetchWorklogTypes() {
  const worklogTypes = yield eff.select(selectors.getResourceMappedList(
    'worklogTypes',
    'worklogTypes',
  ));
  return ({
    options: (
      worklogTypes.map(
        worklogType => ({
          value: worklogType.id,
          label: worklogType.name,
          color: worklogType.color,
          worklogType,
        }),
      )
    ),
    resources: [],
    hasMore: false,
  });
}

function* fetchIssueTypes() {
  const response = yield eff.call(
    jiraApi.getAllIssueTypesForUser,
    {
      params: {},
    },
  );
  const issueTypeProjectIds = [
    ...new Set(
      response.filter(
        i => i?.scope?.type === 'PROJECT',
      ).map(
        i => i.scope.project.id,
      ),
    ),
  ];
  const existProjects = yield eff.select(selectors.getResourceMap('projects'));
  const projects = yield eff.all(
    issueTypeProjectIds
      .map(
        projectIdOrKey => (
          existProjects[projectIdOrKey]
          || (
            eff.call(
              jiraApi.getProjectByIdOrKey,
              {
                params: {
                  projectIdOrKey,
                },
              },
            )
          )
        ),
      ),
  );
  return (
    response.reduce(
      (acc, item) => {
        if (!item.scope) {
          acc.options[0].options.push({
            value: item.id,
            label: item.name,
            item,
          });
        }
        acc.resources.push(item);
        return acc;
      },
      {
        options: ([
          {
            label: 'System types',
            options: [],
          },
          ...projects.map(
            project => ({
              label: project.name,
              options: (
                response.filter(
                  i => (
                    i?.scope?.project.id === project.id
                  ),
                ).map(
                  i => ({
                    label: i.name,
                    value: i.id,
                  }),
                )
              ),
            }),
          ),
        ]),
        resources: [],
        includedResources: {
          projects,
        },
        hasMore: false,
      },
    )
  );
}

const fetchFiltersConfig = jiraType => ({
  users: {
    apiRequest: jiraApi.searchForUsers,
    singleItemApiRequest: jiraApi.getUserByAccountId,
    singleItemApiKey: (
      jiraType === 'server'
        ? 'key'
        : 'accountId'
    ),
    getApiRequestParams: ({ query }) => ({
      /* ^@ - NUL character */
      query: (
        query?.length
          ? query
          : ' '
      ),
      excludeConnectAddons: true,
    }),
    getValues: (r) => (
      r.users.users
    ),
    getResource: item => ({
      id: item.accountId || item.key,
      ...item,
    }),
    getOption: (item) => ({
      value: item.accountId || item.key,
      label: item.displayName,
    }),
    isHasMore: () => false,
  },
  projects: {
    apiRequest: (
      jiraType === 'server'
        ? (
          jiraApi.getAllProjects
        )
        : (
          jiraApi.searchForProjects
        )
    ),
    singleItemApiRequest: jiraApi.getProjectByIdOrKey,
    singleItemApiKey: 'projectIdOrKey',
    getApiRequestParams: ({
      query,
      loadedQty,
    }) => ({
      query,
      startAt: loadedQty,
    }),
    getValues: r => (
      jiraType === 'server'
        ? r
        : r.values
    ),
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    isHasMore: ({
      loadedQty,
      values,
      response,
    }) => (
      loadedQty + values.length !== response.total
    ),
  },
  boards: {
    apiRequest: jiraApi.getAllBoards,
    singleItemApiRequest: jiraApi.getBoardById,
    singleItemApiKey: 'boardId',
    getApiRequestParams: ({
      query,
      loadedQty,
    }) => ({
      query,
      startAt: loadedQty,
    }),
    getValues: r => r.values,
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    isHasMore: ({
      loadedQty,
      values,
      response,
    }) => (
      loadedQty + values.length !== response.total
    ),
  },
  issueStatuses: {
    apiRequest: jiraApi.getAllIssueStatuses,
    getApiRequestParams: () => ({}),
    getValues: r => r,
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    isHasMore: () => false,
  },
  versions: {
    type: 'depending',
    baseApiRequest: fetchFilterResources,
    singleItemApiRequest: jiraApi.getVersionById,
    singleItemApiKey: 'id',
    getBaseApiRequestsParams: ({ loadedQty }) => ({
      query: '',
      loadedQty,
      ...fetchFiltersConfig(jiraType).projects,
    }),
    getBaseValues: r => r.options,
    apiRequest: jiraApi.getProjectVersions,
    getApiRequestParams: ({
      query,
      values,
      item,
    }) => ({
      query,
      projectIdOrKey: item.value,
      startAt: values.length,
    }),
    getResource: item => ({
      projectId: item.projectId,
      ...item,
    }),
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    getIncludedResources: ({ baseResponse }) => ({
      projects: baseResponse.resources,
    }),
    isHasMore: ({ baseResponse }) => baseResponse.hasMore,
  },
  epics: {
    resourceType: 'issues',
    apiRequest: jiraApi.searchForIssues,
    singleItemApiRequest: jiraApi.getIssueByIdOrKey,
    singleItemApiKey: 'issueIdOrKey',
    getValues: r => r.issues,
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: `${item.key} - ${item.fields.summary}`,
      item,
    }),
    getApiRequestParams: ({
      query,
      loadedQty,
    }) => ({
      jql: [
        "issuetype = 'Epic'",
        ...(
          query.length
            ? ([
              `AND (summary ~ "${query}")`,
            ]) : (
              []
            )
        ),
      ].join(' '),
      startAt: loadedQty,
    }),
    isHasMore: ({
      loadedQty,
      values,
      response,
    }) => (
      loadedQty + values.length !== response.total
    ),
  },
  sprints: {
    type: 'depending',
    baseApiRequest: fetchFilterResources,
    singleItemApiRequest: jiraApi.getSprintById,
    singleItemApiKey: 'sprintId',
    getBaseApiRequestsParams: ({
      query,
      loadedQty,
    }) => ({
      query,
      loadedQty,
      ...fetchFiltersConfig(jiraType).boards,
      getApiRequestParams: params => ({
        ...params,
        type: 'scrum',
      }),
    }),
    getBaseValues: r => r.options,
    apiRequest: jiraApi.getAllSprints,
    getApiRequestParams: ({
      values,
      item,
    }) => ({
      boardId: item.value,
      startAt: values.length,
    }),
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    getIncludedResources: ({ baseResponse }) => ({
      boards: baseResponse.resources,
    }),
    isHasMore: ({ baseResponse }) => baseResponse.hasMore,
  },
  issueTypes: {
    type: 'custom',
    fetchRequest: fetchIssueTypes,
  },
  worklogTypes: {
    type: 'custom',
    fetchRequest: fetchWorklogTypes,
  },
  components: {
    type: 'depending',
    baseApiRequest: fetchFilterResources,
    singleItemApiRequest: jiraApi.getComponentById,
    singleItemApiKey: 'id',
    getBaseApiRequestsParams: ({ loadedQty }) => ({
      query: '',
      loadedQty,
      ...fetchFiltersConfig(jiraType).projects,
    }),
    getBaseValues: r => r.options,
    apiRequest: jiraApi.getProjectComponents,
    getApiRequestParams: ({
      query,
      values,
      item,
    }) => ({
      query,
      projectIdOrKey: item.value,
      startAt: values.length,
    }),
    getResource: item => ({
      projectId: item.projectId,
      ...item,
    }),
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    getIncludedResources: ({ baseResponse }) => ({
      projects: baseResponse.resources,
    }),
    isHasMore: ({ baseResponse }) => baseResponse.hasMore,
  },
  jqlFilters: {
    apiRequest: jiraApi.searchForFilters,
    singleItemApiRequest: jiraApi.getFilterById,
    singleItemApiKey: 'id',
    getApiRequestParams: ({
      query,
      loadedQty,
    }) => ({
      filterName: query,
      startAt: loadedQty,
    }),
    getValues: r => r.values,
    getResource: item => item,
    getOption: item => ({
      value: item.id,
      label: item.name,
      item,
    }),
    isHasMore: ({
      loadedQty,
      values,
      response,
    }) => (
      loadedQty + values.length !== response.total
    ),
  },
});

export function* loadFilterOptionsRequest({
  filterType,
  query,
  loadedQty,
  resolve,
}) {
  const jiraType = yield eff.select(selectors.getUiState('jiraType'));
  const fetchFilterConfig = fetchFiltersConfig(jiraType)[filterType];
  const resourceType = fetchFilterConfig.resourceType || filterType;
  const resouceActions = createActionCreators(
    'read',
    {
      resourceType,
      requestKey: 'loadingFilterOptions',
    },
  );
  try {
    yield eff.put(resouceActions.pending());
    const {
      resources,
      includedResources,
      options,
      hasMore,
    } = yield eff.call(
      fetchFilterConfig.fetchRequest || fetchFilterResources,
      {
        query,
        loadedQty,
        ...fetchFilterConfig,
      },
    );
    if (resolve) {
      resolve({
        options,
        hasMore,
      });
    }
    yield eff.put(resouceActions.succeeded({
      resources,
      includedResources: includedResources || {},
    }));
  } catch (err) {
    if (resolve) {
      resolve({
        options: [],
        hasMore: false,
      });
    }
    yield eff.put(resouceActions.succeeded({
      resources: [],
    }));
    throwError(err);
  }
}

export function* fetchFilterResourcesById({ checkFilters }) {
  const jiraType = yield eff.select(selectors.getUiState('jiraType'));
  const filtersWithAllApiRequests = [
    'issueTypes',
    'issueStatuses',
  ];
  const filters = (
    Object.keys(checkFilters).reduce(
      (acc, key) => ({
        ...acc,
        ...(
          Array.isArray(checkFilters[key])
          && checkFilters[key].length
            ? ({
              [key]: checkFilters[key],
            }) : {}
        ),
      }),
      {},
    )
  );
  const filterNames = Object.keys(filters);
  yield eff.all(
    filtersWithAllApiRequests
      .filter(t => filterNames.includes(t))
      .reduce(
        (acc, type) => ({
          ...acc,
          [type]: (
            eff.call(
              loadFilterOptionsRequest,
              {
                filterType: type,
                loadedQty: 0,
              },
            )
          ),
        }),
        {},
      ),
  );
  const resources = yield eff.all(
    filterNames
      .filter(t => !filtersWithAllApiRequests.includes(t))
      .reduce(
        (acc, filterType) => {
          const fetchFilterConfig = fetchFiltersConfig(jiraType)[filterType];
          return ({
            ...acc,
            [filterType]: eff.all(
              filters[filterType].map(
                value => (
                  eff.call(
                    fetchFilterConfig.singleItemApiRequest,
                    {
                      ...(
                        fetchFilterConfig.singleItemApiKey
                          ? ({
                            params: {
                              [fetchFilterConfig.singleItemApiKey]: value,
                            },
                          }) : {}
                      ),
                    },
                  )
                ),
              ),
            ),
          });
        },
        {},
      ),
  );
  yield eff.all(
    Object.keys(resources).reduce(
      (acc, filterType) => {
        const fetchFilterConfig = fetchFiltersConfig(jiraType)[filterType];
        const resourceType = fetchFilterConfig.resourceType || filterType;
        return ({
          ...acc,
          [filterType]: (
            eff.put({
              type: actionTypes.READ_RESOURCES_SUCCEEDED,
              resourceType,
              resources: (
                resources[filterType].map(
                  item => fetchFilterConfig.getResource(item),
                )
              ),
            })
          ),
        });
      },
      {},
    ),
  );
}

export function* takeFetchFilterResourcesByIdRequest() {
    yield eff.takeEvery(
    actions.actionTypes.FETCH_FILTER_RESOURCES_BY_ID_REQUEST,
    fetchFilterResourcesById,
  );
}

export function* takeLoadFilterOptionsRequest() {
  yield eff.takeEvery(
    actions.actionTypes.LOAD_FILTER_OPTIONS_REQUEST,
    loadFilterOptionsRequest,
  );
}
